import type { ParsedNavigationLink } from '@autobid/strapi-integration/typescript/strapi/Navigation'
import type {
  StrapiParsedComponent,
} from '@autobid/strapi-integration/typescript/strapi/Page'
import type { RouteLocationNormalizedLoaded } from 'vue-router'
import type { ComponentExtendsPageContent, LayoutAndNavQuery, Maybe, PageContentSectionsDynamicZone, PdfContentSectionsDynamicZone, PdfFooterSectionsDynamicZone, PdfHeaderSectionsDynamicZone } from '../graphql/graphql'
import { hasUserAccessToThisPage, removeReference } from './helpers'

export function getStrapiLanguage(route: Pick<RouteLocationNormalizedLoaded, 'path' | 'query'>, defaultLang: string, supportedLangs?: string[]) {
  const { path, query } = route

  if (path.includes('/preview')) {
    const previewLang = query.locale

    if (previewLang)
      return previewLang
  }

  const routeParts = path.split('/').filter((el: string) => el.length)
  const routeLang = routeParts?.[0]

  if (!routeLang || (supportedLangs && !supportedLangs.includes(routeLang)))
    return defaultLang

  return routeLang
}

export function convertComponantToTypename(__component: string) {
  const capitalizeName = __component.split(/[.-]/).map((word) => {
    const [first, ...rest] = word.split('')
    return `${first.toUpperCase()}${rest.join('')}`
  }).join('')

  return `Component${capitalizeName}`
}

export type RestComponent = PageContentSectionsDynamicZone & { __component: string }

export type DynamicZonesComponent =
  PageContentSectionsDynamicZone |
  PdfHeaderSectionsDynamicZone |
  PdfContentSectionsDynamicZone |
  PdfFooterSectionsDynamicZone |
  NonNullable<NonNullable<LayoutAndNavQuery['layouts'][number]>['topMenu']>[number]

export function extractStrapiComponents(components: Maybe<Maybe<DynamicZonesComponent>[]> | RestComponent[] | undefined | null): StrapiParsedComponent[] {
  if (!components) {
    return []
  }

  const isExtendsPageContent = (component: DynamicZonesComponent | RestComponent | undefined | null) => {
    if (!component) {
      return false
    }

    if ('__typename' in component) {
      // rest component
      return component.__typename === 'ComponentExtendsPageContent'
    }

    if ('__component' in component) {
      // graphql component
      return component.__component === 'extends.page-content'
    }

    return false
  }

  const allComponents = [
    // get all components expect for DynamicPage
    ...components.filter(component => !isExtendsPageContent(component)),
    // get all components of the DynamicPage components
    ...components
      .filter(isExtendsPageContent)
      .map(
        (component) => {
          // extends.page-content if filtered already
          return (component as unknown as ComponentExtendsPageContent).page?.contentSections || []
        },
      ),
  ].flat()

  return allComponents
    .filter(el => el !== null)
    .map((component): StrapiParsedComponent => {
      if ('__component' in component) {
        component.__typename = convertComponantToTypename(String(component.__component)) as typeof component.__typename
      }

      return {
        id: 'id' in component ? component.id : Math.random().toString(36).substring(2, 15),
        props: component,
      }
    })
}

function setLinkVisiblity(link: LayoutAndNavQuery['renderNavigation'][number], isAuthenticated: boolean): LayoutAndNavQuery['renderNavigation'][number] {
  if (!link) {
    return link
  }

  const copy = removeReference(link)
  let visible = copy.visible

  if (visible && copy.related && 'access' in copy.related) {
    visible = hasUserAccessToThisPage(copy.related.access?.name, isAuthenticated)
  }

  copy.visible = visible

  return copy
}

export function parseStrapiNavigation(isAuthenticated: boolean, navigation?: LayoutAndNavQuery['renderNavigation']) {
  if (!navigation?.length) {
    return undefined
  }

  const parseListToTree = (list: ParsedNavigationLink[]) => {
    const map: Record<number, number> = {}
    let node
    const roots: Record<number, ParsedNavigationLink> = {}
    let i

    for (i = 0; i < list.length; i += 1) {
      if (!list[i]) {
        continue
      }

      map[list[i].id] = i
    }

    for (i = 0; i < list.length; i += 1) {
      node = list[i]

      if (!node) {
        continue
      }

      if (node.parent) {
        list[map[node.parent.id]]._children = {
          ...list[map[node.parent.id]]._children ?? {},
          [node.order]: node,
        }
      }
      else {
        roots[node.order] = node
      }
    }

    return roots
  }

  const result = navigation.map((link): ParsedNavigationLink | null => {
    const item = setLinkVisiblity(link, isAuthenticated)

    if (!item) {
      return null
    }

    return {
      ...item,
      path:
        item.externalPath
        ?? `/${item.related?.locale}/${item.related?.slug}`.replace(
          /\/\//g,
          '/',
        ),
    }
  }).filter(link => link !== null)

  return parseListToTree(result)
}

export function parseStrapiLayouts(layouts: LayoutAndNavQuery['layouts']) {
  if (!layouts?.[0]) {
    return undefined
  }

  return {
    ...layouts[0],
    topMenu: layouts[0].topMenu ? extractStrapiComponents(layouts[0].topMenu) : [],
  }
}
